/**
 * 로비 흐름 - 소켓 방에 앉기까지와 앉은 뒤의 대기실, 그리고 봇 판의 자동 진행.
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(다 모이면 시작) · 7.3절(링크 하나로 들어온다)
 *            `이상한방-상태모델-v0.1.md` 9.2절(인원이 모자라면 봇이 채운다) · 11.3절(시작 가부 · 좌석은 서버가 쥔다)
 *            로드맵 M6(`main.ts` 로비 흐름 - 여럿이 카드 → 로비 → 방 만들기/들어가기 → 시작. 봇 판의 자동 진행 두 줄은 로비로 옮긴다)
 *
 * `main.ts`에서 갈라져 나왔다(그 파일이 이미 1000줄이다). 하는 일은 배선이다 - 전송(`net/wsTransport`)을 만들고, 그 메시지 중 **로비가
 * 그릴 것**(`welcome`·`step`의 뷰 · `error`)만 읽어 화면(`ui/lobby.ts`)과 배너(`ui/connection.ts`)에 옮기고, 손이 누른 것을 `act`로
 * 보낸다. 규칙은 없다 - [시작]이 눌리는가는 뷰의 `canStart`, 이웃을 넣을 수 있는가는 좌석 수와 상한(`MAX_ROOM_SEATS`)의 비교 하나다.
 *
 * ## 세션은 `welcome` 뒤에 선다 - 그래서 `resync`
 *
 * 소켓 방은 내 좌석(`welcome.playerId`)을 알아야 세션을 세울 수 있고, 그 `welcome`은 이 파일이 먼저 받는다. 무대를 세운 뒤 세션은 빈손이라
 * 이 파일이 대신 `resync`를 청한다 - 호스트가 `rooms` + `step`(효과 없음)으로 답하고 세션이 그것으로 첫 뷰를 든다(`matchHost.receive`).
 * 그 뒤의 모든 메시지는 세션이 **직접** 받는다 - 이 파일은 같은 전송을 나란히 구독해 뷰를 읽을 뿐이고, 로비를 벗어나는 `phaseEnter`도
 * 세션이 자기 힘으로 번역한다(자막·카메라·곡). 여기서 세션에 무엇을 "전달"하는 길은 없다 - 그런 길이 생기면 봇 판과 소켓 방의 세션이 다른
 * 순서로 메시지를 본다.
 *
 * **돌아온 `welcome`에도 `resync`를 청한다.** `welcome`은 `atMs`·`phaseElapsedMs`를 싣지 않아 세션의 시계(`game/matchClock`)가
 * 기준을 다시 앉히지 못하는데, 끊긴 동안에도 그 시계는 프레임마다 흘렀다 - 그 흐른 양이 새 뷰에 이미 반영돼 있어 그대로 그리면
 * 같은 시간을 두 번 뺀다. `step` 하나면 기준이 다시 앉는다.
 *
 * ## 봇 판은 같은 로비를 화면 없이 지난다 (`autopilot`)
 *
 * M2~M5에서 세션의 받는 문이 하던 두 줄("welcome에 ready, 전원 준비면 start")이 여기로 왔다. 세션에 두면 소켓 방에서 사람이 [준비]를 누르기
 * 전에 세션이 대신 누른다. 조건은 사람 로비와 **같은 뷰 읽기**다 - `self.canStart`. 호스트가 대신 누르지 않는 이유는 `createBotMatch` 주석에
 * 있다(로비 없는 흐름이 호스트에 굳는다).
 *
 * ## 전송을 닫는 것은 둘이다
 *
 * 무대가 서기 전(폼에서 뒤로 · 서버의 거절)에는 이 파일이 닫고, 선 뒤에는 세션의 `dispose`가 닫는다(`MatchSessionDeps.transport` 주석). 둘 다
 * 부르는 자리(`leaveRoom`)가 있어도 `close`는 두 번 불러도 괜찮다(`Transport.close` 계약).
 *
 * ## 방 목록은 좌석 없는 소켓이다 (M14)
 *
 * `openBrowser`가 `netBoot.browse`로 **좌석 없는 소켓**을 열고(`net/roomBrowse.ts`), 받은 줄을 화면에 옮긴다.
 * 줄을 누르면 `connectAs(code, name)` - **번호로 들어가는 것과 같은 길**이다(M14 규칙 2). 목록 전용 진입로를
 * 만들면 재접속·유예·`superseded`가 한쪽에서만 맞고, 그 어긋남은 "가끔 못 들어간다"로만 보인다.
 *
 * 목록 소켓은 방과 아무 관계가 없어 `link`에 들지 않는다 - 따로 쥐고, 목록 화면을 접을 때 닫는다.
 * 공개 여부는 리듀서에 없으므로(`server/src/room.ts` 머리말) 뷰에 없다 - `welcome.visibility`가 첫 값이고
 * 서버의 `visibility` 메시지가 그 뒤를 갱신한다. 그 값을 `link`가 들고 있다가 로비 얼굴에 얹는다.
 *
 * ## 로비에서 끊긴 자리는 사라진다
 *
 * 리듀서는 로비의 `leave`에 좌석을 지우고 서버는 토큰을 잊는다(`identity.ts`). 그래서 로비 중에 끊겼다 돌아오면 `welcome`이 **다른 좌석**으로
 * 온다. 세션의 `me`는 굳은 값이라 그때는 전부 접고 새 전송으로 다시 앉는다 - 같은 사람이 보기에는 방에 다시 들어온 것이고, 실제로도 그렇다.
 */

import { partyPlanForSeats } from '../core/floorPlan'
import type { PlayerId } from '../core/types'
import type { MatchConfigPatch, PlayerEvent } from '../game/match/matchTypes'
import type { ClientView } from '../game/match/matchView'
import { t } from '../i18n'
import {
  CREATE_ROOM_CODE,
  MAX_ROOM_SEATS,
  type ErrorCode,
  type RoomListing,
  type RoomVisibility,
  type ServerMessage,
} from '../net/protocol'
import type { RoomBrowse } from '../net/roomBrowse'
import type { Transport } from '../net/transport'
import type { WsTransport } from '../net/wsTransport'
import { createConnectionBanner, type ConnectionBannerHandle } from '../ui/connection'
import { createLobby, type LobbyFormError, type LobbyHandle, type LobbyRoomView, type LobbySettingsPatch } from '../ui/lobby'
import { createRoomBrowser, type RoomBrowserHandle, type RoomBrowserState } from '../ui/roomBrowser'
import type { NetBoot } from './netBoot'
import type { MatchStageHandle, MatchStageRequest } from './stage'

/* --- 타입 ------------------------------------------------------------------------ */

export type LobbyFlowDeps = Readonly<{
  mount: HTMLElement
  net: NetBoot
  /**
   * `welcome`을 받았다 - 이 전송 위에 매치 무대를 세우고 손잡이를 돌려준다. `active`가 거짓이면 잠든 채(로비 뒤에서), 참이면 깨어난 채
   * (진행 중인 판으로 돌아왔다). 무대를 갈아 끼우는 것은 앱(`main.swapStage`)이라 여기서는 요청만 넘긴다.
   */
  mountMatch: (request: MatchStageRequest, active: boolean, templateId: string | null) => MatchStageHandle
  /** 로비 화면이 앞에 선다(폼 또는 방). 앱이 `screen`을 `lobby`로 옮기고 다른 오버레이를 접는다. */
  onLobby: () => void
  /** 판으로. 로비가 접혔고 세션이 깨어나야 한다. `resumed`가 참이면 끊겼던 자리로 돌아온 것이다. */
  onPlay: (resumed: boolean) => void
  /** 방을 떠났다(뒤로 · 나가기). 앱이 메뉴로 간다 - 그때 무대가 갈리며 전송도 닫힌다. */
  onLeave: () => void
  /** 방 목록·로비가 앞에 선다(M14). 앱이 `screen`을 옮기고 다른 판을 접는다 - `onLobby`와 같은 자리다. */
  onBrowse: () => void
  /** 방 목록에서 뒤로 - 모두의 방으로. 폼의 [뒤로]와 갈라 둔 이유는 돌아갈 곳이 다르기 때문이다. */
  onBrowseBack: () => void
  /** 방 번호를 주소창에 남긴다(`urlState.writeRoomQuery`). */
  writeRoom: (roomCode: string) => void
}>

export type LobbyFlow = Readonly<{
  /** 폼을 연다. `code`가 있으면 번호 칸을 채운다(`?room=CODE`로 왔지만 좌석 기억이 없다). */
  openForm: (code: string | null) => void
  /** 열린 방 목록을 연다(M14 - 모두의 방의 "아무나 한 판"). */
  openBrowser: () => void
  /** `?room=CODE` 복귀. 이 탭이 그 방에 앉았던 기억이 있으면 곧바로 이어 붙이고 참. 없으면 거짓 - 폼을 열어라. */
  resume: (code: string) => boolean
  /** 봇 판 - 로비 화면 없이 준비·시작을 자동으로. 로비 페이즈를 벗어나면 스스로 구독을 푼다. */
  autopilot: (handle: MatchStageHandle) => () => void
  /**
   * 방을 놓고 로비 화면을 접는다. 부르는 자리는 셋이다: 단계 선택으로 갈 때 · 판을 나갈 때(결과 화면의 [나가기]) · 봇 판으로
   * 갈아탈 때(`?match=bots`·개발 훅). 무대가 갈리면 세션이 전송을 닫지만, 이 파일이 쥔 구독까지 함께 놓아야 다음 방의
   * `welcome`이 옛 링크와 겹치지 않는다.
   */
  leave: () => void
  /** 지금 소켓 방의 연결을 쥐고 있는가. 테스트·개발 훅용. */
  isConnected: () => boolean
  dispose: () => void
}>

/** 지금 앉은(또는 앉으려는) 방의 연결과 그 위에 선 것들. */
type Link = {
  transport: WsTransport
  name: string
  /** 이 연결이 두드린 방 번호(`CREATE_ROOM_CODE`면 새 방). 거절·단절에서 폼의 번호 칸을 되채우는 값이다. */
  requestedCode: string
  unsubscribe: () => void
  handle: MatchStageHandle | null
  /** 지금 서 있는 무대의 평면도 id. 좌석 수가 굳는 순간 이것과 달라지면 무대를 다시 세운다(`remountForSeats`). */
  templateId: string | null
  /** 로비 뒤에 임시로 세운 무대인가. 참일 때만 다시 세울 수 있고, 그 무대는 전송을 소유하지 않는다. */
  provisional: boolean
  me: PlayerId | null
  /** 마지막으로 읽은 뷰. 이웃 이름 번호(`party.botName`)와 로비 얼굴의 재료다. */
  view: ClientView | null
  /** 이 방이 목록에 뜨는가(M14). 첫 값은 `welcome.visibility`, 그 뒤는 서버의 `visibility` 메시지다. */
  visibility: RoomVisibility
}

/* --- 순수 조각 ---------------------------------------------------------------------- */

/**
 * 뷰 → 로비 방 얼굴. 산술은 좌석 수와 상한의 비교 하나다(서버 `room.ts`가 `full`을 내는 기준과 같은 상수 `MAX_ROOM_SEATS`).
 * `canStart`는 뷰의 불리언 그대로다 - 여기서 `players.every(ready)`를 세면 리듀서 게이트의 사본이다.
 */
export function toLobbyRoomView(view: ClientView, visibility: RoomVisibility): LobbyRoomView {
  const isHost = view.hostId === view.me
  const me = view.players.find((player) => player.id === view.me)
  return {
    code: view.roomCode,
    seats: view.players.map((player) => ({
      id: player.id,
      name: player.name,
      isMe: player.id === view.me,
      isHost: player.id === view.hostId,
      isNeighbor: player.kind === 'cpu',
      ready: player.ready,
      connected: player.connected,
    })),
    seatsMax: MAX_ROOM_SEATS,
    isHost,
    meReady: me?.ready ?? false,
    canStart: view.self.canStart,
    canAddNeighbor: isHost && view.players.length < MAX_ROOM_SEATS,
    canRemoveNeighbor: isHost && view.players.some((player) => player.kind === 'cpu'),
    settings: view.config,
    /*
     * 뷰가 아니라 **밖에서** 온다. 공개 여부는 리듀서에 없어(`server/src/room.ts` 머리말) `ClientView`에 실을 수
     * 없고, 그 값을 여기서 지어내면 화면이 서버와 다른 것을 말한다 - 부르는 쪽이 `welcome`·`visibility`로 안 것을 준다.
     */
    visibility,
  }
}

/** 로비 설정 조각 → `configure.patch`. 한 번에 하나만 - 방장이 누른 그 값만 보낸다. */
export function toConfigPatch(patch: LobbySettingsPatch): MatchConfigPatch {
  switch (patch.kind) {
    case 'verbs':
      return { room: { allowedVerbs: new Set(patch.allowedVerbs) } }
    case 'count':
      return { room: { changeCount: patch.changeCount } }
    case 'decorate':
      return { humanDecorate: patch.humanDecorate }
  }
}

/** 다음 이웃의 이름. 서버는 사전이 없어 클라이언트가 자기 언어로 조립해 보낸다(`protocol.ts` 머리말 `lobby.name`). 번호는 앉은 이웃 수 + 1. */
export function nextNeighborName(view: ClientView | null): string {
  const count = view?.players.filter((player) => player.kind === 'cpu').length ?? 0
  return t('party.botName', { n: count + 1 })
}

/* --- 봇 판 자동 진행 ------------------------------------------------------------------ */

/**
 * 로비를 화면 없이 지난다. `welcome`(또는 로비 뷰)에 `ready` 한 번, `canStart`가 참인 뷰에 `start` 한 번. 거절되면(`rejected`) 다음 뷰에서
 * 다시 - 조건은 사람 로비의 [시작] 버튼과 같은 뷰 읽기다.
 */
export function runLobbyAutopilot(transport: Transport, act: (event: PlayerEvent) => number): () => void {
  let readySent = false
  let startCseq: number | null = null

  function onView(view: ClientView): void {
    if (view.phase !== 'lobby') {
      unsubscribe()
      return
    }
    if (!readySent) {
      readySent = true
      act({ type: 'ready', ready: true })
    }
    if (view.self.canStart && startCseq === null) startCseq = act({ type: 'start' })
  }

  const unsubscribe = transport.onMessage((msg: ServerMessage) => {
    if (msg.type === 'welcome' || msg.type === 'step') onView(msg.view)
    else if (msg.type === 'rejected' && msg.cseq === startCseq) startCseq = null
  })
  return unsubscribe
}

/* --- 입구 ------------------------------------------------------------------------ */

export function createLobbyFlow(deps: LobbyFlowDeps): LobbyFlow {
  const banner: ConnectionBannerHandle = createConnectionBanner({ mount: deps.mount })
  let link: Link | null = null
  /** 목록 소켓과 그 화면의 상태. 방과 아무 관계가 없어 `link`에 들지 않는다(머리말). */
  let browse: RoomBrowse | null = null
  let browseState: RoomBrowserState = 'loading'
  let browseRooms: readonly RoomListing[] = []

  const lobby: LobbyHandle = createLobby({
    mount: deps.mount,
    handlers: {
      onCreate: (name) => connectAs(CREATE_ROOM_CODE, name),
      onJoin: (code, name) => connectAs(code, name),
      onBack: () => {
        leaveAll()
        deps.onLeave()
      },
      onAddNeighbor: () => link?.transport.send({ type: 'lobby', op: 'addBot', name: nextNeighborName(link.view) }),
      onRemoveNeighbor: () => link?.transport.send({ type: 'lobby', op: 'removeBot' }),
      onReady: (ready) => link?.handle?.act({ type: 'ready', ready }),
      onStart: () => link?.handle?.act({ type: 'start' }),
      onConfigure: (patch) => link?.handle?.act({ type: 'configure', patch: toConfigPatch(patch) }),
      // 방장만 보내는 것이고 서버가 다시 한 번 가른다 - 화면의 `isHost`는 편의이지 검사가 아니다(M14 규칙 5).
      onVisibility: (value) => link?.transport.send({ type: 'lobby', op: 'setVisibility', visibility: value }),
      onLeave: () => {
        // 스스로 나갔다 - 새로고침이 이 방으로 되돌아가면 안 된다.
        deps.net.forgetSeat()
        leaveAll()
        deps.onLeave()
      },
    },
  })

  const browser: RoomBrowserHandle = createRoomBrowser({
    mount: deps.mount,
    handlers: {
      /*
       * 목록에서 들어가는 것과 번호로 들어가는 것은 **같은 `hello`다**(M14 규칙 2). 그래서 여기서 하는 일은
       * 폼의 [번호로 들어가기]와 한 글자도 다르지 않다 - 목록을 닫고 같은 `connectAs`를 부른다.
       */
      onJoin: (code, name) => {
        closeBrowse()
        connectAs(code, name)
      },
      // 공개 방을 연다. 여기서 만든 방만 목록에 뜬다 - 기본은 비공개다(M14 규칙 4).
      onCreate: (name) => {
        closeBrowse()
        connectAs(CREATE_ROOM_CODE, name, 'public')
      },
      onRefresh: () => browse?.refresh(),
      onBack: () => {
        closeBrowse()
        deps.onBrowseBack()
      },
    },
  })

  /** 목록 소켓과 화면을 함께 놓는다. 화면만 접으면 소켓이 남아 조용히 서버를 두드린다. */
  function closeBrowse(): void {
    browse?.close()
    browse = null
    browser.hide()
  }

  function renderBrowser(): void {
    browser.show({ state: browseState, rooms: browseRooms, name: deps.net.rememberedName() ?? '' })
  }

  function formState(name: string, code: string, error: LobbyFormError | null, busy: boolean): void {
    lobby.showForm({ name, code, error, busy })
    deps.onLobby()
  }

  /**
   * 연결만 놓는다 - 화면은 그대로다. `connectAs`가 두 번째 시도 앞에서 부르는 자리가 있어서(폼이 떠 있어야 한다) 화면 접기와 갈라 둔다.
   * 무대가 서 있으면 세션이 전송을 닫겠지만(앱의 무대 교체) 여기서도 닫는다 - 두 번 닫아도 괜찮다.
   */
  function leaveRoom(): void {
    const current = link
    link = null
    if (current === null) return
    current.unsubscribe()
    current.transport.close()
    banner.hide()
  }

  /** 연결도 놓고 화면도 접는다. 밖에서 부르는 '나가기'는 전부 이것이다 - 목록도 함께 접는다. */
  function leaveAll(): void {
    leaveRoom()
    closeBrowse()
    lobby.hide()
  }

  /** 앉는다. 앞선 연결이 있으면 놓는다(폼에서 두 번째 시도). `visibility`는 새 방을 만들 때만 뜻이 있다(M14). */
  function connectAs(roomCode: string, name: string, visibility?: RoomVisibility): void {
    leaveRoom()
    deps.net.rememberName(name)
    const transport = deps.net.connect({ roomCode, name, ...(visibility === undefined ? {} : { visibility }) })
    const current: Link = {
      transport,
      name,
      requestedCode: roomCode,
      unsubscribe: () => {},
      handle: null,
      templateId: null,
      provisional: false,
      me: null,
      view: null,
      visibility: visibility ?? 'private',
    }
    link = current
    lobby.setError(null)
    lobby.setBusy(true)

    const stopMessages = transport.onMessage((msg) => onMessage(current, msg))
    const stopStatus = transport.onStatus((status, info) => {
      if (link !== current) return
      if (status === 'reconnecting') {
        // 무대가 선 뒤의 끊김만 배너다.
        if (current.handle !== null) {
          banner.show({ kind: 'reconnecting' })
          return
        }
        /*
         * 아직 한 번도 못 앉았는데 소켓이 닫혔다 - 서버가 없거나 닿지 않는다(개발 중 `wierd-room-ws`가 안 떠 있는 자리가 그것이다).
         * 백오프에 맡기면 [방 만들기]가 영원히 "들어가는 중"으로 남고, 그건 사람에게 고장으로 읽힌다. 놓고 이유를 말한다.
         */
        const name = current.name
        leaveRoom()
        formState(name, requestedCodeOf(current), null, false)
        lobby.setError('offline')
        return
      }
      if (status === 'closed' && info.error === null && current.handle !== null) banner.show({ kind: 'closed' })
    })
    current.unsubscribe = () => {
      stopMessages()
      stopStatus()
    }
  }

  /** 폼의 번호 칸에 되돌려 놓을 값. 새 방을 만들려던 것이면 빈 칸이다(그 번호는 아직 없다). */
  function requestedCodeOf(current: Link): string {
    return current.requestedCode === CREATE_ROOM_CODE ? '' : current.requestedCode
  }

  function onMessage(current: Link, msg: ServerMessage): void {
    if (link !== current) return
    switch (msg.type) {
      case 'welcome':
        onWelcome(current, msg)
        return
      case 'step':
        current.view = msg.view
        if (current.handle !== null && lobby.isVisible()) {
          if (msg.view.phase === 'lobby') lobby.showRoom(toLobbyRoomView(msg.view, current.visibility))
          else {
            remountForSeats(current, msg.view)
            enterPlay(false)
          }
        }
        return
      case 'visibility':
        // 서버가 값을 정한다(M14). 방장이 눌러도 화면은 이 메시지가 와야 바뀐다 - 낙관적으로 칠하지 않는다.
        current.visibility = msg.value
        if (current.view !== null && lobby.isVisible() && current.view.phase === 'lobby') {
          lobby.showRoom(toLobbyRoomView(current.view, current.visibility))
        }
        return
      case 'error':
        onError(current, msg.code)
        return
      default:
        return
    }
  }

  function onWelcome(current: Link, msg: Extract<ServerMessage, { type: 'welcome' }>): void {
    current.view = msg.view
    // 옛 서버는 싣지 않는다 - 그때는 비공개다(M14 규칙 4의 기본값이 그대로 여기에도 걸린다).
    current.visibility = msg.visibility ?? 'private'
    if (current.handle !== null) {
      if (current.me === msg.playerId) {
        /*
         * 돌아왔다 - 세션은 같은 welcome을 직접 받아 뷰·방을 다시 든다. 여기가 하는 일은 셋이다.
         *
         * ① `resync`. `welcome`에는 `atMs`·`phaseElapsedMs`가 없어 세션의 시계(`game/matchClock`)가 기준을 다시 앉히지
         *    못한다. 그런데 끊긴 동안에도 세션은 매 프레임 `clock.advance`를 돌렸으므로 `sinceStepMs`가 끊긴 시간만큼
         *    커져 있고, 그 시간은 새 뷰에 **이미 반영돼** 있다 - 그대로 그리면 HUD가 같은 양을 한 번 더 뺀다(끊김이 길면 0).
         *    호스트가 `step`으로 답하면 기준이 다시 앉는다. 조용한 페이즈에서는 그 `step`이 한참 안 오므로 청해야 한다.
         * ② 얼굴. 로비에서 끊긴 사이 방장이 판을 시작했을 수 있다 - 그러면 로비가 아니라 **판**으로 들어가야 한다.
         *    첫 `welcome`(아래)과 같은 판단이라 `phase !== 'lobby'`를 함께 쓴다.
         * ③ 배너. 판으로 들어가는 길은 `enterPlay`가 같은 배너를 띄우므로 여기서 두 번 띄우지 않는다.
         */
        current.transport.send({ type: 'resync' })
        if (msg.view.phase !== 'lobby') {
          remountForSeats(current, msg.view)
          if (lobby.isVisible()) enterPlay(true)
          else banner.show({ kind: 'resumed' })
          return
        }
        banner.show({ kind: 'resumed' })
        if (lobby.isVisible()) lobby.showRoom(toLobbyRoomView(msg.view, current.visibility))
        return
      }
      // 로비에서 끊겨 좌석이 사라졌고 새 좌석으로 앉혀졌다(머리말). 세션의 `me`가 굳어 있으니 새 전송으로 다시 앉는다.
      const name = current.name
      leaveRoom()
      connectAs(msg.roomCode, name)
      return
    }
    current.me = msg.playerId
    const active = msg.view.phase !== 'lobby'
    /*
     * 서버 방의 평면도 id(`WireRoom.templateId`). 로비에서는 방이 아직 없어 `null`이고(진행 중인 방으로 돌아오면 값이 있다), 그때 무대는
     * 기본 평면도로 선다. 이 값을 넘기는 이유는 하나다 - 서버가 세운 방과 다른 크기의 셸·격자로 무대가 서면 소품이 벽을 뚫고 카메라
     * 절두체가 어긋나는데, 그 증상은 "가끔 방이 이상하다"로만 보여 원인 추적이 거의 불가능하다(`app/stage.ts` 머리말의 절두체 절).
     */
    const templateId = msg.rooms.original?.templateId ?? msg.rooms.mutated?.templateId ?? planIdForSeats(msg.view)
    current.templateId = templateId
    current.provisional = !active
    current.handle = deps.mountMatch(
      {
        transport: current.transport,
        me: msg.playerId,
        roomCode: msg.roomCode,
        host: null,
        // 로비 뒤의 무대는 임시다 - 좌석 수가 굳으면 다시 세운다. 그때 이 무대가 소켓을 닫으면 방이 끊긴다.
        retainTransport: !active,
      },
      active,
      templateId,
    )
    // 세션은 이 welcome을 못 봤다(무대가 뒤에 섰다). 같은 것을 다시 청한다 - `rooms` + `step`으로 온다(머리말).
    current.transport.send({ type: 'resync' })
    deps.writeRoom(msg.roomCode)
    lobby.setBusy(false)
    if (active) enterPlay(true)
    else {
      lobby.showRoom(toLobbyRoomView(msg.view, current.visibility))
      deps.onLobby()
    }
  }

  /**
   * 이 뷰의 좌석 수가 만드는 평면도 id. **서버와 같은 함수로 굽는다**(`core/partyPlan` - 난수도 시계도 읽지 않는다).
   *
   * 로비에는 방이 없어(`matchView.roomVisibility`가 `original: false`) `welcome`의 `rooms`가 비어 있고, 그래서 무대가
   * 어떤 평면도로 서야 하는지 알 길이 좌석 수뿐이다. 끊긴 좌석도 세는 것이 맞다 - 리듀서도 진행 중 퇴장에 좌석을
   * 남기므로(`matchState.onLeave`) 양쪽이 같은 수를 센다.
   */
  function planIdForSeats(view: ClientView): string {
    return partyPlanForSeats(view.players.length).id
  }

  /**
   * 좌석 수가 굳었다(로비를 벗어났다) - 임시 무대의 평면도가 그 수와 다르면 다시 세운다(로드맵 M11).
   *
   * 로비에서는 사람이 들락거려 평면도가 흔들리는데, 무대는 [준비]·[시작]을 보낼 손잡이(`handle.act`)가 필요해
   * `welcome` 시점에 이미 서 있어야 한다. 그래서 그 무대는 임시로 두고 여기서 한 번 갈아 끼운다 -
   * 세션은 새로 서면서 `resync`로 방과 뷰를 다시 받는다(재접속과 **같은 경로**다, `app/lobbyFlow` 머리말).
   * 임시 무대가 아니면(이미 진짜 평면도로 섰으면) 아무것도 하지 않는다 - 그 무대는 전송의 주인이라 갈면 소켓이 닫힌다.
   */
  function remountForSeats(current: Link, view: ClientView): void {
    if (!current.provisional || current.me === null) return
    const templateId = planIdForSeats(view)
    current.provisional = false
    if (templateId === current.templateId) return
    current.templateId = templateId
    current.handle = deps.mountMatch(
      { transport: current.transport, me: current.me, roomCode: view.roomCode, host: null },
      true,
      templateId,
    )
    // 새 세션은 빈손이다 - 로비가 처음 앉을 때와 같은 이유로 방·뷰를 대신 청한다(머리말 "세션은 welcome 뒤에 선다").
    current.transport.send({ type: 'resync' })
  }

  function enterPlay(resumed: boolean): void {
    lobby.hide()
    deps.onPlay(resumed)
    if (resumed) banner.show({ kind: 'resumed' })
  }

  function onError(current: Link, code: ErrorCode): void {
    if (current.handle === null || lobby.isVisible()) {
      // 아직 못 앉았거나 로비에서 거절됐다 - 폼으로 돌아와 이유를 보인다. 서버가 끊을 것이므로 여기서도 놓는다.
      const name = current.name
      const codeForForm = requestedCodeOf(current)
      leaveRoom()
      formState(name, codeForForm, code, false)
      return
    }
    // 판 도중의 거절(다른 창이 좌석을 가져갔다) - 세션의 HUD도 같은 사전으로 말하고, 배너는 남는다.
    banner.show({ kind: 'error', code })
  }

  return {
    openForm(code): void {
      leaveRoom()
      closeBrowse()
      formState(deps.net.rememberedName() ?? '', code ?? '', null, false)
    },

    openBrowser(): void {
      leaveRoom()
      browse?.close()
      browseState = 'loading'
      browseRooms = []
      /*
       * 소켓을 먼저 연다. `onList`가 곧바로(마이크로태스크로) 올 수 있고, 그때 `browse`가 아직 `null`이면
       * 다시 그리는 자리가 지금 열린 소켓을 모른다. 화면은 그 뒤에 세운다 - `loading`으로 한 번, 답이 오면 다시.
       */
      browse = deps.net.browse({
        onList: (rooms) => {
          browseRooms = rooms
          browseState = 'list'
          if (browser.isVisible()) renderBrowser()
        },
        onStatus: (status) => {
          if (status === 'closed') browseState = 'offline'
          else if (status === 'connecting' && browseRooms.length === 0) browseState = 'loading'
          if (browser.isVisible()) renderBrowser()
        },
      })
      lobby.hide()
      renderBrowser()
      deps.onBrowse()
    },

    resume(code): boolean {
      const seat = deps.net.rememberedSeat(code)
      if (seat === null) return false
      /*
       * 얼굴을 **먼저** 세운다. `connectAs`의 `setBusy(true)`는 `root.hidden`을 건드리지 않으므로, 폼을 펴지 않으면
       * "방에 들어가는 중"이 숨겨진 로비에 칠해진다 - `?room=` 직행에는 타이틀도 배경 무대도 없어 그 사이 화면이 통째로
       * 비어 있다(닿지 않는 호스트는 TCP 타임아웃까지 검은 화면이다). `welcome`이 오면 `showRoom`·`enterPlay`가 덮는다.
       */
      formState(seat.name, code, null, true)
      connectAs(code, seat.name)
      return true
    },

    autopilot: (handle) => runLobbyAutopilot(handle.transport, handle.act),
    leave: leaveAll,
    isConnected: () => link !== null,

    dispose(): void {
      leaveAll()
      browser.dispose()
      lobby.dispose()
      banner.dispose()
    },
  }
}
